import React, { useState, useEffect, useCallback } from 'react';
import { Save, Plus, Trash2, DollarSign, Fuel, Package, FileText, CheckCircle, AlertTriangle, RefreshCw, Receipt } from 'lucide-react';
import { fetchUpsContractPricing, saveUpsContractPricing, fetchUpsBillActualDiscounts } from '../services/api';

const money = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ACTUAL_GROUPS = [
  { kind: 'transportation', label: 'Transportation (service discount)' },
  { kind: 'accessorial', label: 'Accessorials' },
];

// Expected contract discount for a billed service, read from the loaded
// contract rows. Returns { text, lo } — `lo` is the lower-bound % used to
// flag when UPS billed below contract. Ground is a range: the volume tier
// stacked with the weight/zone incentive (no single contract figure).
function expectedDiscount(service, contractRows) {
  const s = (service || '').toLowerCase();
  const num = (v) => {
    if (v == null) return null;
    const n = parseFloat(String(v).replace(/[^0-9.].*$/, ''));
    return Number.isFinite(n) ? n : null;
  };
  const find = (needle) => {
    const r = (contractRows || []).find(x => (x.name || '').toLowerCase().includes(needle));
    return r ? r.value : null;
  };
  const pt = (needle) => { const v = num(find(needle)); return v == null ? null : { text: `${v}%`, lo: v }; };

  if (s.includes('next day air saver')) return pt('next day air saver');
  if (s.includes('next day air'))       return pt('next day air');
  if (s.includes('2nd day air a.m'))    return pt('2nd day air a.m');
  if (s.includes('2nd day air'))        return pt('2nd day air');
  if (s.includes('3 day select'))       return pt('3 day select');
  if (s.includes('ground')) {
    // Return shipments aren't clearly covered by the outbound Ground
    // incentive — don't flag them against the standard Ground rate.
    if (s.includes('return')) return null;
    const comm = s.includes('commercial');
    const tier = num(find(comm ? 'ground - commercial (volume tier)' : 'ground - residential (volume tier)'));
    const wRaw = find(comm ? 'ground - commercial (weight' : 'ground - residential (weight');
    if (tier == null || !wRaw) return null;
    const m = String(wRaw).match(/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/);
    if (!m) return null;
    const t = tier / 100, wLo = +m[1] / 100, wHi = +m[2] / 100;
    const lo = Math.round((1 - (1 - t) * (1 - wLo)) * 1000) / 10;
    const hi = Math.round((1 - (1 - t) * (1 - wHi)) * 1000) / 10;
    return { text: `~${lo}-${hi}%`, lo };
  }
  if (s.includes('residential surcharge'))   return pt('residential surcharge');
  if (s.includes('delivery area surcharge')) return pt('delivery area surcharge');
  if (s.includes('saturday'))                return pt('saturday delivery');
  if (s.includes('print label') || s.includes('return label')) return pt('print return label');
  if (s.includes('fuel surcharge'))          return pt('fuel surcharge discount');
  return null;
}

// Section definitions. Keys are stored in the `section` column.
const SECTIONS = [
  { key: 'service_discounts', label: 'Service Discounts', icon: Package,
    hint: 'Your negotiated discount (or flat rate) per UPS service.' },
  { key: 'fuel', label: 'Fuel Surcharge', icon: Fuel,
    hint: 'Current UPS fuel surcharge — UPS changes these weekly; update as needed.' },
  { key: 'accessorials', label: 'Accessorial Rates', icon: DollarSign,
    hint: 'Surcharge rates and any contract waivers or caps.' },
  { key: 'terms', label: 'DIM, Minimums & Contract Terms', icon: FileText,
    hint: 'Dimensional divisor, minimum charges, tiers, account #, contract dates.' },
];

// Starting template — the standard UPS contract line items. Greg fills in
// values, edits labels, removes what doesn't apply, adds anything custom.
const SEED = [
  ['service_discounts', 'Ground — Commercial', '%'],
  ['service_discounts', 'Ground — Residential', '%'],
  ['service_discounts', 'Ground — Returns', '%'],
  ['service_discounts', 'Next Day Air', '%'],
  ['service_discounts', 'Next Day Air Saver', '%'],
  ['service_discounts', 'Next Day Air Early', '%'],
  ['service_discounts', '2nd Day Air', '%'],
  ['service_discounts', '2nd Day Air A.M.', '%'],
  ['service_discounts', '3 Day Select', '%'],
  ['service_discounts', 'International — Worldwide Express', '%'],
  ['service_discounts', 'International — Worldwide Saver', '%'],
  ['fuel', 'Ground Fuel Surcharge', '%'],
  ['fuel', 'Air Fuel Surcharge', '%'],
  ['fuel', 'Import / Export Fuel Surcharge', '%'],
  ['accessorials', 'Residential Surcharge', '$'],
  ['accessorials', 'Delivery Area Surcharge — Residential', '$'],
  ['accessorials', 'Delivery Area Surcharge — Commercial', '$'],
  ['accessorials', 'Delivery Area Surcharge — Extended Residential', '$'],
  ['accessorials', 'Delivery Area Surcharge — Extended Commercial', '$'],
  ['accessorials', 'Delivery Area Surcharge — Remote Residential', '$'],
  ['accessorials', 'Delivery Area Surcharge — Remote Commercial', '$'],
  ['accessorials', 'Additional Handling — Weight', '$'],
  ['accessorials', 'Additional Handling — Dimensions', '$'],
  ['accessorials', 'Additional Handling — Packaging', '$'],
  ['accessorials', 'Large Package Surcharge', '$'],
  ['accessorials', 'Over Maximum Limits', '$'],
  ['accessorials', 'Declared Value — rate per $100', '$/$100'],
  ['accessorials', 'Declared Value — minimum charge', '$'],
  ['accessorials', 'Saturday Delivery', '$'],
  ['accessorials', 'Saturday Pickup', '$'],
  ['accessorials', 'Address Correction', '$'],
  ['accessorials', 'Shipping Charge Correction Audit Fee', '$'],
  ['accessorials', 'Peak / Demand — Residential', '$'],
  ['accessorials', 'Peak / Demand — Additional Handling', '$'],
  ['accessorials', 'Peak / Demand — Large Package', '$'],
  ['accessorials', 'Adult Signature Required', '$'],
  ['accessorials', 'Signature Required', '$'],
  ['terms', 'Dimensional Weight Divisor', 'divisor'],
  ['terms', 'Minimum Package Charge — Ground', '$'],
  ['terms', 'Minimum Package Charge — Air', '$'],
  ['terms', 'Earned / Portfolio Discount Tier', 'text'],
  ['terms', 'Weekly Service Charge', '$'],
  ['terms', 'Pickup Fee', '$'],
  ['terms', 'UPS Account Number', 'text'],
  ['terms', 'Contract Effective Date', 'date'],
  ['terms', 'Contract Expiry Date', 'date'],
  ['terms', 'Annual GRI / Rate Year', 'text'],
];

let uidCounter = 1;
const uid = () => `r${uidCounter++}`;
const seedRows = () => SEED.map(([section, name, unit]) => ({ _id: uid(), section, name, value: '', unit, notes: '' }));

export default function UpsContractPricing() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);
  const [seeded, setSeeded] = useState(false);
  const [actuals, setActuals] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, act] = await Promise.all([
        fetchUpsContractPricing(),
        fetchUpsBillActualDiscounts().catch(() => []),
      ]);
      setActuals(Array.isArray(act) ? act : []);
      if (data.length > 0) {
        setRows(data.map(r => ({
          _id: uid(), section: r.section, name: r.name || '',
          value: r.value || '', unit: r.unit || '', notes: r.notes || '',
        })));
        setSeeded(false);
      } else {
        setRows(seedRows());
        setSeeded(true);
      }
      setDirty(false);
    } catch (e) {
      setError(e.message || 'Failed to load contract pricing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateRow = (id, field, val) => {
    setRows(rs => rs.map(r => (r._id === id ? { ...r, [field]: val } : r)));
    setDirty(true);
    setSavedMsg(null);
  };
  const removeRow = (id) => {
    setRows(rs => rs.filter(r => r._id !== id));
    setDirty(true);
    setSavedMsg(null);
  };
  const addRow = (section) => {
    setRows(rs => [...rs, { _id: uid(), section, name: '', value: '', unit: '', notes: '' }]);
    setDirty(true);
    setSavedMsg(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    try {
      const payload = rows.map(({ section, name, value, unit, notes }) => ({ section, name, value, unit, notes }));
      const res = await saveUpsContractPricing(payload);
      setSavedMsg(`Saved ${res.saved} variable${res.saved === 1 ? '' : 's'}.`);
      setDirty(false);
      setSeeded(false);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const filled = rows.filter(r => (r.value || '').trim() !== '').length;

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>UPS Contract Pricing</h1>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4, maxWidth: 720 }}>
            Your negotiated UPS contract terms — every factor that affects the bill. Stored for
            reconciliation analysis. Fill in what applies, edit labels, and add or remove rows freely.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {savedMsg && (
            <span style={{ fontSize: 12, color: '#1a8a4a', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <CheckCircle size={14} /> {savedMsg}
            </span>
          )}
          {dirty && !savedMsg && (
            <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Unsaved changes</span>
          )}
          <button className="btn btn-primary" style={{ fontSize: 13, padding: '7px 16px' }}
            disabled={saving || loading || !dirty} onClick={save}>
            <Save size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {seeded && !loading && (
        <div className="card" style={{ marginBottom: 14, padding: '10px 14px', fontSize: 12, color: 'var(--navy)', background: '#eef4fb', borderLeft: '3px solid var(--navy)' }}>
          This is a starting template of the standard UPS contract line items — nothing is saved yet.
          Fill in your numbers, remove anything that doesn't apply, then <strong>Save changes</strong>.
        </div>
      )}
      {error && (
        <div className="card" style={{ marginBottom: 14, padding: 12, fontSize: 12, color: 'var(--red)' }}>
          <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {error}
        </div>
      )}

      {actuals.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Receipt size={15} style={{ color: 'var(--orange)' }} />
            <span>Your actual discounts — from your latest UPS bill</span>
            <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: 12 }}>
              · invoice {actuals[0].invoice_number}
              {actuals[0].invoice_date ? ` · ${new Date(actuals[0].invoice_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
              {' '}— the discount UPS actually applied (list vs. what you paid). Compare it to your contract bands below.
            </span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <table className="product-table" style={{ width: '100%', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Service / Charge</th>
                  <th style={{ textAlign: 'right' }}>Packages</th>
                  <th style={{ textAlign: 'right' }}>UPS list</th>
                  <th style={{ textAlign: 'right' }}>You paid</th>
                  <th style={{ textAlign: 'right' }}>Expected discount</th>
                  <th style={{ textAlign: 'right' }}>Actual discount</th>
                </tr>
              </thead>
              <tbody>
                {ACTUAL_GROUPS.map(({ kind, label }) => {
                  const g = actuals.filter(a => a.kind === kind);
                  if (!g.length) return null;
                  return (
                    <React.Fragment key={kind}>
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--gray-100, #f5f5f5)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--gray-500)' }}>
                          {label}
                        </td>
                      </tr>
                      {g.map((a, i) => {
                        const exp = expectedDiscount(a.service, rows);
                        const act = a.discount_pct == null ? null : Number(a.discount_pct);
                        const short = exp && act != null && act < exp.lo - 2;
                        return (
                          <tr key={kind + i}>
                            <td>{a.service}</td>
                            <td style={{ textAlign: 'right' }}>{a.packages}</td>
                            <td style={{ textAlign: 'right' }}>{money(a.list)}</td>
                            <td style={{ textAlign: 'right' }}>{money(a.net)}</td>
                            <td style={{ textAlign: 'right', color: 'var(--navy)' }}>{exp ? exp.text : '—'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color: act == null ? 'var(--gray-400)' : short ? 'var(--red)' : '#1a8a4a' }}>
                              {act == null ? '—' : `${a.discount_pct}%`}
                              {short && <span title="Below contract" style={{ marginLeft: 4 }}>▼</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '8px 14px', fontSize: 11, color: 'var(--gray-500)' }}>
            Discount = the $ UPS knocked off ÷ the published list charge, from this invoice's standard
            charge lines (corrections/adjustments excluded). <strong>Expected</strong> is your contract
            discount (from the rows below); Ground is a range — volume tier stacked with the weight/zone
            incentive. An actual that falls more than 2 points below expected is flagged in red ▼.
          </div>
        </div>
      )}

      {loading ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
          <RefreshCw size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Loading…
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 10 }}>
            {rows.length} variable{rows.length === 1 ? '' : 's'} · {filled} filled in
          </div>
          {SECTIONS.map(sec => {
            const Icon = sec.icon;
            const secRows = rows.filter(r => r.section === sec.key);
            return (
              <div key={sec.key} className="card" style={{ marginBottom: 16 }}>
                <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon size={15} style={{ color: 'var(--orange)' }} />
                  <span>{sec.label}</span>
                  <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: 12 }}>· {sec.hint}</span>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table className="product-table" style={{ width: '100%', fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', width: '34%' }}>Variable</th>
                        <th style={{ textAlign: 'left', width: '15%' }}>Value</th>
                        <th style={{ textAlign: 'left', width: '12%' }}>Unit</th>
                        <th style={{ textAlign: 'left' }}>Notes</th>
                        <th style={{ width: 36 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {secRows.length === 0 && (
                        <tr><td colSpan={5} style={{ padding: 12, color: 'var(--gray-500)' }}>
                          No variables in this section — use "Add variable" below.
                        </td></tr>
                      )}
                      {secRows.map(r => (
                        <tr key={r._id}>
                          <td>
                            <input className="form-input" value={r.name}
                              placeholder="Variable name"
                              onChange={e => updateRow(r._id, 'name', e.target.value)}
                              style={{ fontSize: 12, padding: '4px 8px', width: '100%' }} />
                          </td>
                          <td>
                            <input className="form-input" value={r.value}
                              placeholder="—"
                              onChange={e => updateRow(r._id, 'value', e.target.value)}
                              style={{ fontSize: 12, padding: '4px 8px', width: '100%', fontWeight: 700, color: 'var(--navy)' }} />
                          </td>
                          <td>
                            <input className="form-input" value={r.unit}
                              placeholder="%, $, …"
                              onChange={e => updateRow(r._id, 'unit', e.target.value)}
                              style={{ fontSize: 12, padding: '4px 8px', width: '100%' }} />
                          </td>
                          <td>
                            <input className="form-input" value={r.notes}
                              placeholder="Optional — waivers, caps, tiers, conditions…"
                              onChange={e => updateRow(r._id, 'notes', e.target.value)}
                              style={{ fontSize: 12, padding: '4px 8px', width: '100%' }} />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <Trash2 size={14} style={{ color: 'var(--gray-400)', cursor: 'pointer' }}
                              onClick={() => removeRow(r._id)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: '8px 14px' }}>
                  <button className="btn btn-outline" style={{ fontSize: 11, padding: '4px 10px' }}
                    onClick={() => addRow(sec.key)}>
                    <Plus size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Add variable
                  </button>
                </div>
              </div>
            );
          })}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
            <button className="btn btn-primary" style={{ fontSize: 13, padding: '7px 16px' }}
              disabled={saving || !dirty} onClick={save}>
              <Save size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
